// Detalle de encuesta: métricas, Publicación (URL + script), Vista previa, Estadísticas por pregunta y Respuestas.
import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download, ExternalLink, Pencil } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Loading, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { CopyBox, Metric, Tabs, Toggle, copyText, downloadCsv, pct, type Links } from "../forms/shared.tsx";
import { SurveyPreview } from "./SurveyPreview.tsx";
import { QTYPE_LABEL, type Question, type QuestionType, type Survey } from "./types.ts";

type Tab = "publicacion" | "preview" | "estadisticas" | "respuestas";
const TABS: Tab[] = ["publicacion", "preview", "estadisticas", "respuestas"];

interface StatOption { label: string; count: number; pct: number }
interface QuestionStat {
  id: string; label: string; type: QuestionType; answered: number; options?: StatOption[]; avg?: number | null; samples?: string[];
  nps?: { score: number; promoters: number; passives: number; detractors: number };
}
type Answer = string | string[] | number;
interface ResponseRow {
  id: string; createdAt: string; answers: Record<string, Answer> | null; utm: Record<string, string> | null;
  contactId: string | null; contactCreated: boolean; contactName: string | null; contactEmail: string | null;
}

function answerText(q: Question, a: Answer | undefined): string {
  if (a === undefined || a === "") return "—";
  if (Array.isArray(a)) return a.join("; ");
  if (q.type === "yesno") return a === "yes" ? "Sí" : "No";
  return String(a);
}

export function SurveyDetail(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const admin = Boolean(useMe().data?.admin);
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("tab") as Tab;
  const tab: Tab = TABS.includes(raw) ? raw : "publicacion";
  const setTab = (t: Tab): void => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n, { replace: true }); };

  const q = useQuery<{ survey: Survey; links: Links }>({ queryKey: ["mkt-survey", id], queryFn: () => api.get(`/mkt/surveys/${id}`) });
  const publish = useMutation({
    mutationFn: (published: boolean) => api.post(`/mkt/surveys/${id}/publish`, { published }),
    onSuccess: (_r, published) => {
      toast.success(published ? "Encuesta publicada." : "Encuesta despublicada (borrador).");
      void qc.invalidateQueries({ queryKey: ["mkt-survey", id] }); void qc.invalidateQueries({ queryKey: ["mkt-surveys"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción."),
  });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar la encuesta." onRetry={() => void q.refetch()} />;
  const { survey, links } = q.data;
  const published = survey.status === "published";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" className="btn-ghost btn-sm" onClick={() => nav("..")} aria-label="Volver"><ArrowLeft size={16} /></button>
          <h2 className="text-lg font-semibold text-ink truncate">{survey.name}</h2>
          <Badge color={published ? "green" : "gray"}>{published ? "Publicada" : "Borrador"}</Badge>
        </div>
        <div className="flex gap-2 items-center">
          {admin && <Toggle checked={published} disabled={publish.isPending} onChange={(v) => publish.mutate(v)} label={published ? "Publicada" : "Borrador"} />}
          {published && <a className="btn-secondary" href={links.pageUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={16} /> Ver</a>}
          {admin && <Button onClick={() => nav("editar")}><Pencil size={16} /> Editar</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Impresiones" value={survey.impressions} />
        <Metric label="Respuestas" value={survey.responsesCount} hint={`Tasa: ${pct(survey.responsesCount, survey.impressions)}`} onClick={() => setTab("respuestas")} active={tab === "respuestas"} />
        <Metric label="Contactos creados" value={survey.contactsCreated} />
        <Metric label="Preguntas" value={survey.questions.length} />
      </div>

      <Card className="p-0 overflow-hidden min-w-0">
        <div className="px-4 pt-2"><Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: "publicacion", label: "Publicación" }, { id: "preview", label: "Vista previa" }, { id: "estadisticas", label: "Estadísticas" }, { id: "respuestas", label: "Respuestas" }]} /></div>
        <div className="p-4">
          {tab === "publicacion" && <PublicationTab published={published} links={links} />}
          {tab === "preview" && <SurveyPreview questions={survey.questions} settings={survey.settings} />}
          {tab === "estadisticas" && <StatsTab id={id} />}
          {tab === "respuestas" && <ResponsesTab survey={survey} />}
        </div>
      </Card>
    </div>
  );
}

function PublicationTab({ published, links }: { published: boolean; links: Links }): JSX.Element {
  return (
    <div className="space-y-5">
      {!published && <div className="text-sm rounded-md bg-warning-soft text-ink px-3 py-2">La encuesta está en borrador: publícala para que la URL y el código funcionen.</div>}
      <div>
        <div className="label">URL pública</div>
        <div className="flex flex-wrap gap-2 items-center">
          <code className="text-xs bg-canvas border border-line rounded px-2 py-1.5 break-all flex-1 min-w-0">{links.pageUrl}</code>
          <Button small onClick={() => void copyText(links.pageUrl, "URL copiada")}>Copiar</Button>
          <a className="btn-secondary btn-sm" href={links.pageUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Ver</a>
        </div>
      </div>
      <CopyBox label="Código para incrustar (pégalo donde quieras que aparezca la encuesta)" value={links.embedCode} />
    </div>
  );
}

function StatsTab({ id }: { id: string }): JSX.Element {
  const q = useQuery<{ total: number; questions: QuestionStat[] }>({ queryKey: ["mkt-survey-stats", id], queryFn: () => api.get(`/mkt/surveys/${id}/stats`) });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudieron cargar las estadísticas." onRetry={() => void q.refetch()} />;
  if (!q.data.total) return <EmptyState title="Sin respuestas" body="Las estadísticas aparecerán cuando alguien responda la encuesta." />;
  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-muted">Basado en {q.data.total} {q.data.total === 1 ? "respuesta" : "respuestas"}.</p>
      {q.data.questions.map((s) => (
        <section key={s.id} className="border-t border-line pt-4 first:border-0 first:pt-0">
          <div className="flex flex-wrap gap-2 justify-between items-baseline">
            <h3 className="font-medium text-ink">{s.label}</h3>
            <span className="text-xs text-ink-muted">{QTYPE_LABEL[s.type]} · {s.answered} {s.answered === 1 ? "respuesta" : "respuestas"}</span>
          </div>
          {(s.avg !== undefined && s.avg !== null || s.nps) && (
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              {s.avg !== undefined && s.avg !== null && <span>Promedio: <strong className="tnum">{s.avg}</strong></span>}
              {s.nps && <span>NPS: <strong className="tnum">{s.nps.score}</strong> <span className="text-ink-muted">({s.nps.promoters} promotores · {s.nps.passives} pasivos · {s.nps.detractors} detractores)</span></span>}
            </div>
          )}
          {s.options && (
            <div className="space-y-1.5 mt-3">
              {s.options.map((o) => (
                <div key={o.label} className="flex items-center gap-3 text-sm">
                  <div className="w-40 text-ink-secondary shrink-0 truncate" title={o.label}>{o.label}</div>
                  <div className="flex-1 h-4 bg-canvas rounded overflow-hidden"><div className="h-full bg-primary/80 rounded" style={{ width: `${o.pct}%` }} /></div>
                  <div className="w-24 text-right tnum text-ink">{o.count} <span className="text-xs text-ink-muted">· {o.pct}%</span></div>
                </div>
              ))}
            </div>
          )}
          {s.samples && (s.samples.length
            ? <ul className="mt-3 space-y-1 text-sm text-ink-secondary list-disc pl-5">{s.samples.map((t, i) => <li key={i} className="break-words">{t}</li>)}</ul>
            : <p className="mt-2 text-sm text-ink-muted">Sin respuestas todavía.</p>)}
        </section>
      ))}
    </div>
  );
}

function ResponsesTab({ survey }: { survey: Survey }): JSX.Element {
  const [page, setPage] = useState(1);
  const q = useQuery<{ rows: ResponseRow[]; total: number; pageSize: number }>({
    queryKey: ["mkt-survey-responses", survey.id, page],
    queryFn: () => api.get(`/mkt/surveys/${survey.id}/responses?page=${page}`),
  });
  const pages = Math.max(1, Math.ceil((q.data?.total ?? 0) / (q.data?.pageSize ?? 50)));
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button small onClick={() => void downloadCsv(`/mkt/surveys/${survey.id}/responses.csv`, `respuestas-${survey.name.replace(/[^\w-]+/g, "-").slice(0, 50)}.csv`)}><Download size={14} /> Exportar CSV</Button>
      </div>
      {q.isLoading ? <TableSkeleton rows={4} cols={3} />
        : q.isError ? <ErrorState message="No se pudieron cargar las respuestas." onRetry={() => void q.refetch()} />
        : !q.data?.rows.length ? <EmptyState title="Sin respuestas" body="Cuando alguien responda la encuesta aparecerá aquí." />
        : (
          <div className="overflow-x-auto scrollbar-thin -mx-4">
            <table className="w-full">
              <thead><tr><th className="th">Fecha</th><th className="th">Contacto</th><th className="th">Respuestas</th></tr></thead>
              <tbody>
                {q.data.rows.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="td whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                    <td className="td">
                      {r.contactId ? <Link className="link" to={`/contacts/${r.contactId}`}>{r.contactName ?? r.contactEmail ?? "Contacto"}</Link> : "Anónimo"}
                      {r.contactEmail && <div className="text-xs text-ink-muted">{r.contactEmail}</div>}
                      {r.contactCreated && <div className="mt-1"><Badge color="blue">Nuevo</Badge></div>}
                    </td>
                    <td className="td text-xs max-w-xl">
                      {survey.questions.map((qn) => <div key={qn.id} className="break-words"><span className="text-ink-muted">{qn.label}:</span> {answerText(qn, r.answers?.[qn.id])}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {pages > 1 && (
        <div className="flex justify-end items-center gap-2 text-sm">
          <Button small disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="text-ink-muted">Página {page} de {pages}</span>
          <Button small disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
